import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { ChevronDown, ChevronUp, Maximize2, Minimize2, Table2 } from "lucide-react";
import { useLanguage } from "@/i18n";

/** Below this the table's own header and pager leave no room for rows. */
const MIN = 0.4;
const MAX = 0.85;
/** Opening gives the table the larger share: the map shrinks to the top. */
const DEFAULT_OPEN = 0.6;
const BAR_PX = 44;
/** How much of the area the table keeps when it makes way for a zoom. */
const REVEAL = 0.45;
/** The open/close/resize slide (duration-300), plus a frame. */
const SLIDE_MS = 320;

export type FieldDataDrawerHandle = {
  /**
   * Makes room for the map before zooming to something from the table: a
   * table taking more than REVEAL of the area slides down to it. Returns how
   * long to wait before zooming (0 when nothing moved), so the map zooms at
   * its new size rather than the strip it was.
   */
  revealMap: () => number;
};
const KEY = "gs-field-drawer";

function readState(): { open: boolean; height: number } {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "null");
    if (v && typeof v.open === "boolean" && typeof v.height === "number") {
      return { open: v.open, height: Math.min(MAX, Math.max(MIN, v.height)) };
    }
  } catch {
    // ignore
  }
  return { open: false, height: DEFAULT_OPEN };
}

/**
 * The Field data table in the bottom of the map area. Closed it is a bar
 * with the count; tapping it slides the table up and the map shrinks above
 * it (40-85% of the height, drag the handle to adjust, or maximise). The
 * table scrolls on its own -- the drawer itself never scrolls, so there is
 * one scrollbar, not two fighting each other.
 */
const FieldDataDrawer = forwardRef<FieldDataDrawerHandle, {
  /** e.g. "Field data · 1,992" */
  title: string;
  children: ReactNode;
  /** Told when the table opens or closes, so the page can make room. */
  onOpenChange?: (open: boolean) => void;
}>(function FieldDataDrawer({ title, children, onOpenChange }, ref) {
  const { t } = useLanguage();
  const [{ open, height }, setState] = useState(readState);
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef({ open, height });
  stateRef.current = { open, height };

  useImperativeHandle(ref, () => ({
    revealMap: () => {
      const s = stateRef.current;
      if (!s.open || s.height <= REVEAL) return 0;
      setState((cur) => ({ ...cur, height: REVEAL }));
      return SLIDE_MS;
    },
  }));

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ open, height }));
    } catch {
      // ignore
    }
  }, [open, height]);

  function startDrag(e: React.PointerEvent) {
    const parent = boxRef.current?.parentElement;
    if (!parent) return;
    e.preventDefault();
    const rect = parent.getBoundingClientRect();
    setDragging(true);
    let frame = 0;
    const move = (ev: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const h = (rect.bottom - ev.clientY) / rect.height;
        setState((s) => ({ ...s, height: Math.min(MAX, Math.max(MIN, h)) }));
      });
    };
    const up = () => {
      cancelAnimationFrame(frame);
      setDragging(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  const maximised = open && height >= MAX - 0.01;

  return (
    <div
      ref={boxRef}
      className={clsx(
        "relative flex flex-none flex-col border-t border-line bg-surface shadow-[0_-8px_20px_-10px_rgba(15,23,42,0.3)]",
        // Smooth open / close / maximise; off while dragging so it follows the pointer.
        !dragging && "transition-[height] duration-300 ease-smooth will-change-[height] motion-reduce:transition-none"
      )}
      style={{ height: open ? `${height * 100}%` : `${BAR_PX}px` }}
    >
      {open && (
        <div
          role="separator"
          aria-orientation="horizontal"
          title={t.workspace.dragToResize}
          onPointerDown={startDrag}
          className="group absolute inset-x-0 -top-1.5 z-10 flex h-3 cursor-row-resize items-center justify-center"
        >
          <span
            className={clsx(
              "h-1 w-12 rounded-full transition",
              dragging ? "bg-accent-solid" : "bg-line-strong group-hover:bg-accent-solid"
            )}
          />
        </div>
      )}

      <div className="flex h-11 flex-none items-center gap-2 px-4">
        <button
          type="button"
          onClick={() => setState((s) => ({ open: !s.open, height: s.open ? s.height : Math.max(s.height, DEFAULT_OPEN) }))}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left text-xs font-bold uppercase tracking-wider text-ink"
        >
          <Table2 className="h-4 w-4 flex-none text-accent-solid" />
          <span className="truncate">{title}</span>
        </button>
        {open && (
          <button
            type="button"
            onClick={() => setState((s) => ({ ...s, height: maximised ? DEFAULT_OPEN : MAX }))}
            title={maximised ? t.workspace.restoreTable : t.workspace.maximiseTable}
            aria-label={maximised ? t.workspace.restoreTable : t.workspace.maximiseTable}
            className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
          >
            {maximised ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        )}
        <button
          type="button"
          onClick={() => setState((s) => ({ open: !s.open, height: s.open ? s.height : Math.max(s.height, DEFAULT_OPEN) }))}
          className="flex h-7 items-center gap-1 rounded-md px-2 text-2xs font-semibold text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
        >
          {open ? t.workspace.hideTable : t.workspace.showTable}
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
      </div>

      {/* Kept mounted while closed so the table keeps its search, page and
          edits; hidden rather than removed. */}
      <div className={clsx("min-h-0 flex-1 overflow-hidden px-3 pb-3", !open && "invisible")}>
        {children}
      </div>
    </div>
  );
});

export default FieldDataDrawer;
